import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, ArrowRight, HelpCircle, Heart, Shield } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export default function HowItWorksPage() {
  const steps = [
    {
      number: '01',
      title: 'Créez votre compte',
      description: 'Inscrivez-vous gratuitement et recevez 10 crédits de bienvenue pour commencer à échanger immédiatement.',
      icon: Users,
    },
    {
      number: '02',
      title: 'Proposez ou recherchez',
      description: 'Publiez vos services ou parcourez ceux de la communauté. Jardinage, cours, bricolage... tout est possible !',
      icon: Heart,
    },
    {
      number: '03',
      title: 'Échangez du temps',
      description: '1 heure de service = 1 crédit temps. Donnez une heure, recevez un crédit. Simple et équitable.',
      icon: Clock,
    },
  ];

  const values = [
    {
      icon: '🤝',
      title: 'Entraide',
      description: 'Nous croyons en la force de la communauté et de la solidarité entre voisins.',
    },
    {
      icon: '⚖️',
      title: 'Égalité',
      description: 'Tous les services ont la même valeur : 1 heure = 1 crédit, sans distinction.',
    },
    {
      icon: '🌱',
      title: 'Durabilité',
      description: 'Favoriser le réemploi des compétences locales plutôt que la consommation.',
    },
    {
      icon: '💚',
      title: 'Confiance',
      description: 'Une communauté basée sur la confiance mutuelle et le respect.',
    },
  ];

  const faqs = [
    {
      question: "Comment fonctionne le système de crédits ?",
      answer: "Chaque heure de service rendu vous rapporte 1 crédit temps. Ces crédits peuvent ensuite être utilisés pour bénéficier des services d'autres membres. Vous recevez 10 crédits de bienvenue à l'inscription.",
    },
    {
      question: "Qui peut s'inscrire sur Oknî ?",
      answer: "Oknî est ouvert à tous les habitants d'Ottignies-Louvain-la-Neuve et des environs. L'inscription est gratuite et sans engagement.",
    },
    {
      question: "Est-ce que tous les services ont la même valeur ?",
      answer: "Oui ! C'est le principe fondamental d'une banque du temps. Qu'il s'agisse d'une heure de cours de maths ou d'une heure de jardinage, la valeur est la même : 1 heure = 1 crédit.",
    },
    {
      question: "Comment sont protégés mes échanges ?",
      answer: "Les crédits ne sont transférés qu'une fois l'échange confirmé par le prestataire. Les deux parties peuvent laisser un avis, et un système de signalement permet de gérer les problèmes.",
    },
    {
      question: "Que faire si un échange se passe mal ?",
      answer: "Vous pouvez signaler tout problème via notre système de signalement. Notre équipe examine chaque cas et prend les mesures nécessaires.",
    },
    {
      question: "Les crédits expirent-ils ?",
      answer: "Non, vos crédits n'expirent jamais. Vous pouvez les utiliser quand vous le souhaitez.",
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-background">
        <div className="container-app text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Comment fonctionne <span className="text-gradient">Oknî</span> ?
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Une banque du temps simple et conviviale pour échanger des services 
            entre voisins à Ottignies-Louvain-la-Neuve.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="relative rounded-2xl border-0 bg-card shadow-lg overflow-hidden">
                <CardContent className="p-8">
                  <div className="absolute top-4 right-4 text-6xl font-bold text-primary/10">
                    {step.number}
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                    <step.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos valeurs</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Oknî est plus qu'une plateforme, c'est une communauté basée sur des valeurs fortes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="rounded-2xl text-center">
                <CardContent className="p-6">
                  <div className="text-4xl mb-4">{value.icon}</div>
                  <h3 className="text-lg font-semibold mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24">
        <div className="container-app max-w-3xl">
          <div className="text-center mb-12">
            <HelpCircle className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Questions fréquentes</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border rounded-xl px-6"
              >
                <AccordionTrigger className="text-left font-medium hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24">
        <div className="container-app">
          <Card className="rounded-3xl bg-primary text-primary-foreground overflow-hidden">
            <CardContent className="p-8 md:p-12 lg:p-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Prêt à rejoindre la communauté ?
              </h2>
              <p className="text-lg opacity-90 mb-8 max-w-xl mx-auto">
                Inscrivez-vous gratuitement et commencez à échanger avec vos voisins.
              </p>
              <Link to="/signup">
                <Button className="bg-white text-primary hover:bg-white/90 rounded-full text-lg px-8 py-6">
                  <Clock className="w-5 h-5 mr-2" />
                  S'inscrire - 10 crédits offerts
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
}
